import React, { ReactNode, useEffect, useState } from 'react';
import wretch from 'wretch';
import { useBuildVars } from '../build-config';
import { SiteConfig, siteConfigContext } from './site-config-context';
import { getSameOriginApiBasePath } from '../../query/utils';
import { disableSameOriginRequests } from '../../query/query-consts';
import { v4 as uuidV4 } from 'uuid';
import { ErrorView } from '../../x4ec/routing/error';

const apiWretcher = wretch(undefined).options({
  // https://developer.mozilla.org/en-US/docs/Web/API/Request/credentials
  // credentials: 'include',
  credentials: 'same-origin', // Default

  headers: {
    'IM-CorrelationID': uuidV4(),
    accept: 'application/json',
    'content-type': 'application/json',
  },

  // https://developer.mozilla.org/en-US/docs/Web/API/Request/mode
  mode: 'cors',
});

type Props = {
  children: ReactNode;
};

export function SiteConfigProvider(props: Props) {
  const { children } = props;

  const buildVars = useBuildVars();
  const isX4EC = buildVars.appId === 'x4ec';

  // TODO: Same-origin Config API cannot be enabled per country as config call contains siteCode.
  //   This logic should be removed once once same-origin APIs is rollouted to ALL countries.
  const shouldUseSameOriginApis = () => {
    if (isX4EC && buildVars.buildEnvironment !== 'development') return true;

    return (
      buildVars.buildEnvironment !== 'production' &&
      buildVars.buildEnvironment !== 'productionbeta' &&
      buildVars.buildEnvironment !== 'development' &&
      localStorage.getItem(disableSameOriginRequests) !== 'true'
    );
  };

  const [value, setValue] = useState<SiteConfig | undefined>(undefined);
  const [errorX4EC, setErrorX4EC] = useState<404 | 403 | undefined>();

  useEffect(() => {
    let active = true;

    /**
     * Config API response is being cached. The 'ver' query param would be used to invalidate cache if necessary
     */
    const queryParams: { [key: string]: string | undefined } = {
      ver: buildVars['CONFIG_API_VERSION'],
    };

    (async () => {
      const isLocalDev = globalThis.location.origin.indexOf('://local') > -1;
      const apiBase = isLocalDev ? '/api-ingram-proxy' : buildVars.API_BASE;

      const appId = buildVars.appId === 'cep' ? 'x4c' : buildVars.appId;
      let wretcher = apiWretcher.url(
        `${
          shouldUseSameOriginApis() ? getSameOriginApiBasePath() : apiBase
        }/imonline-k8s-cep/cwc/config/v2/config/${appId}`
      );
      // TODO: When same-origin APIs is rolled out to all countries on prod, the above wretcher definition
      //   can be replaced with the below code to enable it for all envs, all countries.
      // let wretcher = apiWretcher.url(
      //   `${getSameOriginApiBasePath()/imonline-k8s-cep/cwc/config/v1/config`
      // );

      if (isLocalDev) {
        /**
         * Note: hostname query param is always needed when running locally.
         * Even when running stage and production builds locally in express or ngnix.
         */
        queryParams['hostname'] =
          buildVars.appId === 'x4ec'
            ? buildVars.X4EC_DEV_PROXY_HOSTNAME
            : buildVars.DEV_PROXY_HOSTNAME;
      }

      wretcher = wretcher.query(queryParams);

      const siteConfig = await wretcher
        .get()
        .error(404, (_) => {
          if (isX4EC) setErrorX4EC(404); // 404 = store not found
        })
        .error(403, (_) => {
          if (isX4EC) setErrorX4EC(403); // 403 = store not live
        })
        .json<SiteConfig>();

      if (active) {
        setValue(siteConfig);
      }
    })();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildVars, setValue]);

  if (!value) {
    // In X4EC, show error view if the site config fetch fails
    if (errorX4EC) return <ErrorView statusCode={errorX4EC} />;

    // Do not render anything while the site config is being fetched
    return null;
  }

  const { Provider } = siteConfigContext;
  return <Provider value={value}>{children}</Provider>;
}
